// 업그레이드 관리 클래스
class UpgradeManager {
  constructor(game) {
    this.game = game;
    this.player = game.player;
    this.uiManager = game.uiManager;

    // 용사 클래스 업그레이드 레벨 추적
    this.knightUpgradeLevels = {
      SWORD: 0,
      SHIELD: 0,
    };

    // 동료 시스템 (궁수, 바바리안, 마법사)
    // 각 동료는 개별 레벨을 가지며, 여러 동료 소환 가능
    this.companions = {
      ROGUE: [], // 각 요소는 { level: number }
      BARBARIAN: [],
      MAGE: [],
    };

    // 파티 버프 레벨 추적 (Party.partyBuffCoefficients를 업데이트하기 위한 레벨 정보)
    this.partyBuffLevels = {
      ATTACK_DAMAGE: 0,
      ATTACK_RANGE: 0,
      ATTACK_COOLTIME: 0,
      MOVE_SPEED: 0,
      MAX_HP: 0,
    };

    // 게임 초기 스탯 저장 (용사 업그레이드 계산의 기준점, 절대 변하지 않음)
    // 주의: player.baseStats와는 다름!
    // - UpgradeManager.baseStats: 게임 초기값 (용사 업그레이드 계산 기준)
    // - player.baseStats: 용사 업그레이드가 반영된 값 (파티 버프 적용 기준)
    this.baseStats = {
      attackDamage: PlayerConfig.ATTACK_DAMAGE,
      attackRange: PlayerConfig.ATTACK_RANGE,
      attackAngle: PlayerConfig.ATTACK_ANGLE,
      attackCooltime: PlayerConfig.ATTACK_COOLTIME,
      maxHealth: PlayerConfig.MAX_HEALTH,
      moveSpeed: PlayerConfig.MOVE_SPEED,
    };

    // 초기 무기/방패 설정 (게임 시작 시 호출)
    this.initializeWeapons();
  }

  // 초기 무기/방패 설정
  initializeWeapons() {
    // 현재 레벨에 맞는 무기와 방패 설정
    this.updatePlayerWeapon();
    this.updatePlayerShield();
  }

  // 레벨업 시 업그레이드 선택지 표시
  showUpgradeOptions() {
    // 게임 일시정지
    this.game.pause();
    Utils.log("레벨업 창 표시 - 게임 일시정지");

    // 사용 가능한 업그레이드 옵션 생성
    const options = this.generateUpgradeOptions();

    // 업그레이드 선택지 즉시 표시 (카메라 회전 없음)
    this.uiManager.showUpgradeSelection(options, (selectedUpgrade) => {
      // 업그레이드 적용
      this.applyUpgrade(selectedUpgrade);

      // 업그레이드 선택 후 다음 레벨업 체크 (비동기로 처리)
      setTimeout(() => {
        this.game.resume();
        this.checkAndShowNextLevelUp();
      }, 50); // 짧은 딜레이 후 다음 레벨업 체크 (레벨업 후 지연시간 줄이기)
    });
  }

  // 다음 레벨업 체크 (연속 레벨업 처리)
  checkAndShowNextLevelUp() {
    // 한국어 주석: 업그레이드 창이 열려있을 때 쌓인 경험치 재체크
    if (this.player && this.player.recheckLevelUp) {
      this.player.recheckLevelUp();
    }

    // 남은 포인트로 추가 레벨업이 가능한지 체크
    if (
      this.player &&
      this.player.points >= this.player.pointsToNextLevel &&
      this.player.level < GameConfig.LEVEL_SYSTEM.MAX_LEVEL
    ) {
      // 포인트 차감 및 레벨 증가
      this.player.points -= this.player.pointsToNextLevel;
      this.player.level++;
      this.player.pointsToNextLevel = this.player.calculatePointsToNextLevel();

      Utils.log(`레벨업! 레벨 ${this.player.level} 달성!`);

      // 레벨업 시 체력 10% 회복
      const healAmount = Math.floor(this.player.maxHealth * 0.2);
      this.player.heal(healAmount);

      // 연속 레벨업 - 업그레이드 선택 창 다시 표시
      this.showUpgradeOptions();
    } else {
      // 더 이상 레벨업 불가 - 게임 재개
      this.game.resume();
      Utils.log("레벨업 완료 - 게임 재개");

      // 한국어 주석: 게임 재개 후에도 한 번 더 레벨업 재체크 (혹시 모를 경험치 누락 방지)
      setTimeout(() => {
        if (this.player && this.player.recheckLevelUp) {
          this.player.recheckLevelUp();
        }
      }, 50); // 레벨업 후 지연시간 줄이기
    }
  }

  // 업그레이드 옵션 생성 (설정에서 지정한 개수만큼)
  generateUpgradeOptions() {
    const allOptions = [];

    // 용사 클래스 업그레이드 추가
    this.addKnightUpgradeOptions(allOptions);

    // 동료 시스템 옵션 추가
    this.addCompanionOptions(allOptions);

    // 파티 버프 옵션 추가
    this.addPartyBuffOptions(allOptions);

    // 랜덤하게 선택 (OPTIONS_COUNT 개수만큼)
    return this.selectRandomOptions(allOptions, UpgradeConfig.OPTIONS_COUNT);
  }

  // 랜덤 옵션 선택
  selectRandomOptions(allOptions, count) {
    const selectedOptions = [];
    const optionsCopy = [...allOptions];

    for (let i = 0; i < Math.min(count, optionsCopy.length); i++) {
      const randomIndex = Math.floor(Math.random() * optionsCopy.length);
      selectedOptions.push(optionsCopy[randomIndex]);
      optionsCopy.splice(randomIndex, 1);
    }

    return selectedOptions;
  }

  // 용사 클래스 업그레이드 옵션 추가
  addKnightUpgradeOptions(allOptions) {
    const knightConfig = UpgradeConfig.CLASSES.KNIGHT;

    // 검 업그레이드
    if (
      this.knightUpgradeLevels.SWORD < knightConfig.UPGRADES.SWORD.MAX_LEVEL
    ) {
      const currentLevel = this.knightUpgradeLevels.SWORD;
      allOptions.push({
        id: "KNIGHT_SWORD",
        className: knightConfig.NAME,
        name: knightConfig.UPGRADES.SWORD.NAME,
        level: currentLevel + 1,
        color: knightConfig.COLOR,
        description: knightConfig.UPGRADES.SWORD.DESCRIPTIONS[currentLevel],
      });
    }

    // 방패 업그레이드
    if (
      this.knightUpgradeLevels.SHIELD < knightConfig.UPGRADES.SHIELD.MAX_LEVEL
    ) {
      const currentLevel = this.knightUpgradeLevels.SHIELD;
      allOptions.push({
        id: "KNIGHT_SHIELD",
        className: knightConfig.NAME,
        name: knightConfig.UPGRADES.SHIELD.NAME,
        level: currentLevel + 1,
        color: knightConfig.COLOR,
        description: knightConfig.UPGRADES.SHIELD.DESCRIPTIONS[currentLevel],
      });
    }
  }

  // 동료 옵션 추가
  addCompanionOptions(allOptions) {
    const companions = UpgradeConfig.COMPANIONS;

    Object.keys(companions).forEach((companionKey) => {
      const companionConfig = companions[companionKey];
      const companionsList = this.companions[companionKey];

      // 동료가 없거나, 마지막 동료가 최대 레벨이면 새 동료 생성 옵션 추가
      const lastCompanion = companionsList[companionsList.length - 1];
      const canCreateNew =
        companionsList.length === 0 ||
        (lastCompanion && lastCompanion.level >= companionConfig.MAX_LEVEL);

      if (canCreateNew) {
        allOptions.push({
          id: `COMPANION_CREATE_${companionKey}`,
          className: companionConfig.NAME,
          name: `${companionConfig.NAME}`,
          level: 1,
          color: companionConfig.COLOR,
          description:
            companionConfig.DESCRIPTIONS[0] || "새로운 동료를 고용합니다.",
        });
      }

      // 마지막 동료가 최대 레벨이 아니면 업그레이드 옵션 추가
      if (lastCompanion && lastCompanion.level < companionConfig.MAX_LEVEL) {
        const companionIndex = companionsList.length - 1;
        allOptions.push({
          id: `COMPANION_UPGRADE_${companionKey}_${companionIndex}`,
          className: companionConfig.NAME,
          name: `${companionConfig.NAME} 강화`,
          level: lastCompanion.level + 1,
          color: companionConfig.COLOR,
          description:
            companionConfig.DESCRIPTIONS[lastCompanion.level] ||
            "동료를 강화합니다.",
        });
      }
    });
  }

  // 파티 버프 옵션 추가
  addPartyBuffOptions(allOptions) {
    const partyBuffsConfig = UpgradeConfig.PARTY_BUFFS;
    const buffColor = partyBuffsConfig.COLOR;

    Object.keys(partyBuffsConfig).forEach((buffKey) => {
      // COLOR 키는 건너뛰기
      if (buffKey === "COLOR") return;

      const buffConfig = partyBuffsConfig[buffKey];
      const currentLevel = this.partyBuffLevels[buffKey] || 0;

      // 최대 레벨 도달 시 제외
      if (currentLevel >= buffConfig.MAX_LEVEL) {
        return;
      }

      allOptions.push({
        id: `PARTY_${buffKey}`,
        className: "파티",
        name: buffConfig.NAME,
        level: currentLevel + 1,
        color: buffColor,
        description: buffConfig.DESCRIPTIONS[currentLevel],
      });
    });
  }

  // 업그레이드 적용
  applyUpgrade(upgradeId) {
    Utils.log(`업그레이드 적용: ${upgradeId}`);

    // 파티 버프 처리
    if (upgradeId.startsWith("PARTY_")) {
      const buffKey = upgradeId.replace("PARTY_", "");
      this.applyPartyBuff(buffKey);
      return;
    }

    // 동료 생성 처리
    if (upgradeId.startsWith("COMPANION_CREATE_")) {
      const companionKey = upgradeId.replace("COMPANION_CREATE_", "");
      this.createCompanion(companionKey);
      return;
    }

    // 동료 업그레이드 처리
    if (upgradeId.startsWith("COMPANION_UPGRADE_")) {
      const parts = upgradeId.replace("COMPANION_UPGRADE_", "").split("_");
      const companionKey = parts[0];
      const companionIndex = parseInt(parts[1]);
      this.upgradeCompanion(companionKey, companionIndex);
      return;
    }

    // 용사 업그레이드 처리
    if (upgradeId.startsWith("KNIGHT_")) {
      const upgradeName = upgradeId.replace("KNIGHT_", "");
      this.applyKnightUpgrade(upgradeName);
      return;
    }

    Utils.log(`알 수 없는 업그레이드 ID: ${upgradeId}`);
  }

  // 용사 업그레이드 적용
  applyKnightUpgrade(upgradeName) {
    // 레벨 증가
    this.knightUpgradeLevels[upgradeName]++;

    // 현재 레벨의 효과를 기본값에 적용 (중첩 X)
    this.recalculateKnightStats();

    // 무기/방패 시각적 업데이트
    if (upgradeName === "SWORD") {
      this.updatePlayerWeapon();
    } else if (upgradeName === "SHIELD") {
      this.updatePlayerShield();
    }

    // 업그레이드 후 파티 스탯 디버깅
    this.debugPartyStats("용사 업그레이드");

    // 한국어 주석: 용사 업그레이드 시 카메라 줌 연출 (Config로 제어)
    const knightZoomCfg = GameConfig.CAMERA?.ZOOM?.ON_KNIGHT_UPGRADE;
    if (
      this.game.cameraController &&
      this.player &&
      knightZoomCfg &&
      knightZoomCfg.ENABLED
    ) {
      this.game.cameraController.startZoomOnTarget(
        this.player,
        knightZoomCfg.SCALE ?? 1.2,
        knightZoomCfg.DURATION ?? 1.0
      );
    }
  }

  // 플레이어 무기 업데이트
  updatePlayerWeapon() {
    if (this.player && this.player.updateSword) {
      this.player.updateSword(this.knightUpgradeLevels.SWORD);
    }
  }

  // 플레이어 방패 업데이트
  updatePlayerShield() {
    if (this.player && this.player.updateShield) {
      this.player.updateShield(this.knightUpgradeLevels.SHIELD);
    }
  }

  // 동료 생성 (레벨 1로 시작)
  createCompanion(companionKey) {
    const companionConfig = UpgradeConfig.COMPANIONS[companionKey];

    // 새 동료를 레벨 1로 추가
    this.companions[companionKey].push({ level: 1 });

    // CompanionManager를 통해 실제 동료 소환
    if (this.game.companionManager) {
      this.game.companionManager
        .spawnCompanion(companionKey)
        .then((success) => {
          if (success) {
            Utils.log(`${companionConfig.NAME} 동료 소환 완료!`);

            // 한국어 주석: 동료 생성 시 카메라 줌 연출 (Config로 제어)
            const compZoomCfg = GameConfig.CAMERA?.ZOOM?.ON_COMPANION_SPAWN;
            if (
              this.game.cameraController &&
              compZoomCfg &&
              compZoomCfg.ENABLED
            ) {
              // 방금 생성된 마지막 동료를 찾기
              const companions = this.game.companionManager.companions;
              const lastCompanion = companions[companions.length - 1];
              if (lastCompanion && lastCompanion.isModelLoaded()) {
                this.game.cameraController.startZoomOnTarget(
                  lastCompanion,
                  compZoomCfg.SCALE ?? 1.2,
                  compZoomCfg.DURATION ?? 1.0
                );
              }
            }
          } else {
            Utils.log(`${companionConfig.NAME} 동료 소환 실패`, "warn");
          }
        });
    }

    Utils.log(
      `${companionConfig.NAME} 고용! (총 ${this.companions[companionKey].length}명)`
    );

    // 업그레이드 후 파티 스탯 디버깅
    this.debugPartyStats("동료 생성");
  }

  // 동료 업그레이드 (현재는 MAX_LEVEL이 1이라 사용되지 않음)
  upgradeCompanion(companionKey, companionIndex) {
    const companionConfig = UpgradeConfig.COMPANIONS[companionKey];
    const companionData = this.companions[companionKey][companionIndex];

    if (!companionData) {
      Utils.log(`동료를 찾을 수 없음: ${companionKey} #${companionIndex}`);
      return;
    }

    // 레벨 증가
    companionData.level++;

    // CompanionManager를 통해 실제 동료 업그레이드
    if (this.game.companionManager) {
      const success = this.game.companionManager.upgradeCompanion(
        companionKey,
        companionData.level
      );
      if (success) {
        Utils.log(`${companionConfig.NAME} 동료 업그레이드 완료!`);
      } else {
        Utils.log(`${companionConfig.NAME} 동료 업그레이드 실패`, "warn");
      }
    }

    Utils.log(
      `${companionConfig.NAME} #${companionIndex + 1} 강화! Lv.${
        companionData.level
      }`
    );

    // 업그레이드 후 파티 스탯 디버깅
    this.debugPartyStats("동료 업그레이드");
  }

  // 파티 버프 적용 - 모든 파티 멤버(Player와 Companion)에게 적용됨
  applyPartyBuff(buffKey) {
    const buffConfig = UpgradeConfig.PARTY_BUFFS[buffKey];
    const currentLevel = this.partyBuffLevels[buffKey] || 0;

    // 레벨 증가
    this.partyBuffLevels[buffKey] = currentLevel + 1;

    // 파티 버프 계수 재계산 (중첩 X, 현재 레벨만 적용)
    this.updatePartyBuffCoefficients();

    // Player와 모든 Companion에게 파티 버프 적용
    this.applyPartyBuffsToAllMembers();

    Utils.log(
      `파티 버프: ${buffConfig.NAME} Lv.${this.partyBuffLevels[buffKey]}`
    );

    // 업그레이드 후 파티 스탯 디버깅
    this.debugPartyStats("파티 버프");
  }

  // Party 버프 계수 업데이트 (현재 레벨의 효과만 적용, 중첩 X)
  updatePartyBuffCoefficients() {
    const partyBuffsConfig = UpgradeConfig.PARTY_BUFFS;

    // 한국어 주석: 파티 버프 계수는 0부터 시작해서 더해지는 방식
    // 계수 초기화
    Party.partyBuffCoefficients = {
      ATTACK_DAMAGE: 0,
      ATTACK_RANGE: 0,
      ATTACK_COOLTIME: 0,
      MAX_HEALTH: 0,
      MOVE_SPEED: 0,
    };

    // 각 버프의 현재 레벨에 해당하는 효과만 적용
    Object.keys(partyBuffsConfig).forEach((buffKey) => {
      if (buffKey === "COLOR") return;

      const buffConfig = partyBuffsConfig[buffKey];
      const currentLevel = this.partyBuffLevels[buffKey] || 0;

      // 레벨이 0이면 스킵
      if (currentLevel === 0) return;

      // 현재 레벨의 효과만 가져오기 (배열은 0부터 시작하므로 -1)
      const effect = buffConfig.EFFECTS[currentLevel - 1];
      if (!effect) return;

      // 효과를 계수에 직접 설정 (중첩 X)
      if (effect.ATTACK_DAMAGE !== undefined) {
        Party.partyBuffCoefficients.ATTACK_DAMAGE = effect.ATTACK_DAMAGE;
      }
      if (effect.ATTACK_RANGE !== undefined) {
        Party.partyBuffCoefficients.ATTACK_RANGE = effect.ATTACK_RANGE;
      }
      if (effect.ATTACK_COOLTIME !== undefined) {
        Party.partyBuffCoefficients.ATTACK_COOLTIME = effect.ATTACK_COOLTIME;
      }
      if (effect.MAX_HEALTH !== undefined) {
        Party.partyBuffCoefficients.MAX_HEALTH = effect.MAX_HEALTH;
      }
      if (effect.MOVE_SPEED !== undefined) {
        Party.partyBuffCoefficients.MOVE_SPEED = effect.MOVE_SPEED;
      }
    });
  }

  // 용사 스탯 재계산 (현재 레벨의 효과만 적용, 중첩 X)
  // 항상 UpgradeManager.baseStats(게임 초기값)을 기준으로 계산하므로 중첩되지 않음
  recalculateKnightStats() {
    const knightConfig = UpgradeConfig.CLASSES.KNIGHT;

    // 한국어 주석: 용사 업그레이드는 기본값에 더하는 방식으로 적용
    // 검 업그레이드 효과 적용 (this.baseStats = 게임 초기값)
    const swordLevel = this.knightUpgradeLevels.SWORD;
    if (swordLevel > 0) {
      const swordEffect = knightConfig.UPGRADES.SWORD.EFFECTS[swordLevel - 1];

      if (swordEffect.ATTACK_DAMAGE !== undefined) {
        this.player.attackDamage =
          this.baseStats.attackDamage * (1 + swordEffect.ATTACK_DAMAGE);
      }

      if (swordEffect.ATTACK_COOLTIME !== undefined) {
        this.player.attackCooltime =
          this.baseStats.attackCooltime * (1 + swordEffect.ATTACK_COOLTIME);

        // 공격 애니메이션 속도 조정
        if (this.player.attackAction) {
          const duration = this.player.attackAction.getClip().duration;
          this.player.attackAction.setEffectiveTimeScale(
            duration / this.player.attackCooltime
          );
        }
      }
    } else {
      // 한국어 주석: 검 레벨이 0이면 기본값으로 복원
      this.player.attackDamage = this.baseStats.attackDamage;
      this.player.attackCooltime = this.baseStats.attackCooltime;

      if (this.player.attackAction) {
        const duration = this.player.attackAction.getClip().duration;
        this.player.attackAction.setEffectiveTimeScale(
          duration / this.player.attackCooltime
        );
      }
    }

    // 방패 업그레이드 효과 적용
    const shieldLevel = this.knightUpgradeLevels.SHIELD;
    if (shieldLevel > 0) {
      const shieldEffect =
        knightConfig.UPGRADES.SHIELD.EFFECTS[shieldLevel - 1];

      if (shieldEffect.MAX_HEALTH !== undefined) {
        const oldMaxHealth = this.player.maxHealth;
        this.player.maxHealth =
          this.baseStats.maxHealth * (1 + shieldEffect.MAX_HEALTH);

        // 현재 체력도 비율 유지하며 증가
        const healthRatio = this.player.health / oldMaxHealth;
        this.player.health = this.player.maxHealth * healthRatio;

        // 체력바 갱신
        if (this.player.healthBar) {
          this.player.healthBar.updateHealth(
            this.player.health,
            this.player.maxHealth
          );
        }
      }
    } else {
      // 한국어 주석: 방패 레벨이 0이면 기본값으로 복원
      const oldMaxHealth = this.player.maxHealth;
      this.player.maxHealth = this.baseStats.maxHealth;

      const healthRatio = this.player.health / oldMaxHealth;
      this.player.health = this.player.maxHealth * healthRatio;

      if (this.player.healthBar) {
        this.player.healthBar.updateHealth(
          this.player.health,
          this.player.maxHealth
        );
      }
    }

    // 한국어 주석: 용사 업그레이드에 없는 속성들(attackRange, moveSpeed 등)은 기본값으로 복원
    // 이렇게 해야 용사 업그레이드를 해도 이동속도나 범위가 갑자기 달라지지 않음
    this.player.attackRange = this.baseStats.attackRange;
    this.player.moveSpeed = this.baseStats.moveSpeed;

    // Player의 baseStats 업데이트 후 파티 버프 재적용
    // 1. updateBaseStats(): 현재 player 스탯을 player.baseStats에 저장 (용사 업그레이드 반영)
    // 2. applyPartyBuffs(): player.baseStats를 기준으로 파티 버프 재적용
    if (this.player && this.player.updateBaseStats) {
      this.player.updateBaseStats();
      this.player.applyPartyBuffs();
    }
  }

  // 모든 파티 멤버에게 파티 버프 적용
  applyPartyBuffsToAllMembers() {
    // Player에게 적용
    if (this.player && this.player.applyPartyBuffs) {
      this.player.applyPartyBuffs();
    }

    // 모든 Companion에게 적용
    if (this.game.companionManager && this.game.companionManager.companions) {
      this.game.companionManager.companions.forEach((companion) => {
        if (companion && companion.applyPartyBuffs && companion.isAlive) {
          companion.applyPartyBuffs();
        }
      });
    }
  }

  // 업그레이드 시스템 리셋 (게임 재시작 시)
  reset() {
    // 용사 업그레이드 초기화
    this.knightUpgradeLevels = {
      SWORD: 0,
      SHIELD: 0,
    };

    // 동료 시스템 초기화
    this.companions = {
      ROGUE: [],
      BARBARIAN: [],
      MAGE: [],
    };

    // 파티 버프 레벨 초기화
    this.partyBuffLevels = {
      ATTACK_DAMAGE: 0,
      ATTACK_RANGE: 0,
      ATTACK_COOLTIME: 0,
      MOVE_SPEED: 0,
      MAX_HP: 0,
    };

    // 한국어 주석: 파티 버프 계수 초기화
    Party.partyBuffCoefficients = {
      ATTACK_DAMAGE: 0,
      ATTACK_RANGE: 0,
      ATTACK_COOLTIME: 0,
      MAX_HEALTH: 0,
      MOVE_SPEED: 0,
    };

    // 한국어 주석: 플레이어 스탯을 초기값으로 리셋
    this.resetPlayerToBaseStats();

    Utils.log("업그레이드 시스템 리셋 완료");
  }

  // 플레이어 스탯을 초기값으로 리셋
  resetPlayerToBaseStats() {
    if (!this.player) return;

    // 기본 스탯으로 리셋
    this.player.attackDamage = this.baseStats.attackDamage;
    this.player.attackCooltime = this.baseStats.attackCooltime;
    this.player.maxHealth = this.baseStats.maxHealth;
    this.player.health = this.player.maxHealth;

    // Player의 baseStats 업데이트
    if (this.player.updateBaseStats) {
      this.player.updateBaseStats();
    }

    // 체력바 업데이트
    if (this.player.healthBar) {
      this.player.healthBar.updateHealth(
        this.player.health,
        this.player.maxHealth
      );
    }

    // 공격 애니메이션 속도 초기화
    if (this.player.attackAction) {
      const duration = this.player.attackAction.getClip().duration;
      this.player.attackAction.setEffectiveTimeScale(
        duration / this.player.attackCooltime
      );
    }

    Utils.log("플레이어 스탯 초기화 완료");
  }

  // 현재 업그레이드 상태 조회
  getUpgradeStatus() {
    return {
      knightLevels: { ...this.knightUpgradeLevels },
      companions: {
        ROGUE: [...this.companions.ROGUE],
        BARBARIAN: [...this.companions.BARBARIAN],
        MAGE: [...this.companions.MAGE],
      },
      partyBuffLevels: { ...this.partyBuffLevels },
      baseStats: { ...this.baseStats },
    };
  }

  // 파티 스탯 디버깅 (업그레이드 후 호출)
  debugPartyStats(upgradeType) {
    // 디버그 모드가 활성화되지 않았으면 스킵
    if (!GameConfig.DEBUG || !GameConfig.DEBUG.ENABLED) return;

    console.log(`\n========== 파티 스탯 (${upgradeType}) ==========`);

    // 파티 버프 계수 출력
    console.log("📊 파티 버프 계수:");
    console.table(Party.partyBuffCoefficients);

    // 플레이어 스탯 출력
    if (this.player) {
      console.log("\n⚔️ 플레이어 스탯:");
      console.log("  기본 스탯:", this.player.baseStats);
      console.log("  현재 스탯:", {
        attackDamage: this.player.attackDamage,
        attackRange: this.player.attackRange,
        attackCooltime: this.player.attackCooltime,
        maxHealth: this.player.maxHealth,
        moveSpeed: this.player.moveSpeed,
        health: this.player.health,
      });
    }

    // 동료들 스탯 출력
    if (this.game.companionManager && this.game.companionManager.companions) {
      const companions = this.game.companionManager.companions;
      if (companions.length > 0) {
        console.log("\n🤝 동료 스탯:");
        companions.forEach((companion, index) => {
          console.log(`  동료 #${index} (${companion.constructor.name}):`);
          console.log("    기본 스탯:", companion.baseStats);
          console.log("    현재 스탯:", {
            attackDamage: companion.attackDamage,
            attackRange: companion.attackRange,
            attackCooltime: companion.attackCooltime,
            maxHealth: companion.maxHealth,
            moveSpeed: companion.moveSpeed,
            health: companion.health,
          });
        });
      }
    }

    console.log("==========================================\n");
  }
}

// webpack 빌드를 위해 default export 추가
export default UpgradeManager;
